import { useEffect, useRef, useState } from 'react'
import { CircleAlert, ImageIcon, Save, Upload } from 'lucide-react'
import { api } from '@/util/api'
import { EventLogoError, prepareEventLogo } from '@/lib/eventLogo'
import { Autocomplete, Button, IconButton, Switch, TextAreaField, TextField, useToast } from '@/components/play'
import type { EventResource } from '@/types/events'
import { WorkspaceInfo } from '@/components/workspace/WorkspaceInfo'
import { IngestRegionPicker } from '@/components/IngestRegionPicker'
import { DEFAULT_INGEST_REGION } from '@/hooks/useIngestRegions'
import '@/components/settings/event-settings.css'

/** Every IANA time zone the browser knows, and the browser's own, for the event's time zone field. */
const browserZone = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
})()
const TIME_ZONES: readonly string[] = (() => {
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.('timeZone')
  return supported?.length ? supported : browserZone ? [browserZone] : []
})()
const zoneLabel = (zone: string) => zone.replace(/_/g, ' ')
/** The zones matching what's typed ("cape", "johannes", "new york"), at most 8. */
function zonesMatching(typed: string): string[] {
  const query = typed.trim().toLowerCase().replace(/_/g, ' ')
  if (!query) return []
  return TIME_ZONES.filter((zone) => zoneLabel(zone).toLowerCase().includes(query)).slice(0, 8)
}

/** A logo that won't draw, such as an SVG the API serves as a download (uploads are PNG now: lib/eventLogo). */
const LOGO_UNSHOWN = 'This logo can’t be shown. Upload it again.'

interface Props {
  eventId: string
  event: EventResource | null
  onSaved?: (next: EventResource) => void
}

interface ApiError {
  response?: { data?: { error?: { message?: string; errors?: string[] } } }
}

export function EventGeneralForm({ eventId, event, onSaved }: Props) {
  const { toast } = useToast()
  const [name, setName] = useState('')
  const [shortcode, setShortcode] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [description, setDescription] = useState('')
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [timezone, setTimezone] = useState('')
  const [timezoneError, setTimezoneError] = useState<string | null>(null)
  const [liveOnly, setLiveOnly] = useState(false)
  const [listed, setListed] = useState(false)
  const [logoInHeader, setLogoInHeader] = useState(false)
  const [ingestRegion, setIngestRegion] = useState(DEFAULT_INGEST_REGION)
  const [saving, setSaving] = useState(false)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  // Why the chosen file can't be the logo, and a preview that won't draw: each says so under the logo.
  const [logoRefused, setLogoRefused] = useState<string | null>(null)
  const [brokenPreview, setBrokenPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!event) return
    const attrs = event.attributes
    setName(String(attrs.name ?? ''))
    setShortcode(String(attrs.shortcode ?? ''))
    setSubtitle(String(attrs.subtitle ?? ''))
    setDescription(String(attrs.description ?? ''))
    setStartsAt(toLocalDatetime(attrs.starts_at))
    setEndsAt(toLocalDatetime(attrs.ends_at))
    // An event without one starts from the organiser's own: saving keeps it unless they change it.
    setTimezone(zoneLabel(attrs.timezone ?? browserZone))
    setTimezoneError(null)
    setLiveOnly(attrs.live_only === true)
    setListed(attrs.visibility === 'listed')
    setLogoInHeader(attrs.logo_in_header === true)
    setIngestRegion(attrs.ingest_region || DEFAULT_INGEST_REGION)
    setLogoFile(null)
    setLogoPreview((attrs.logo_url as string | null) ?? null)
    setLogoRefused(null)
  }, [event])

  // An SVG uploads as the PNG drawn from it, and a file over the budget is refused here, saying what to upload.
  async function onLogoSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const upload = await prepareEventLogo(file)
      setLogoRefused(null)
      setLogoFile(upload)
      setLogoPreview(URL.createObjectURL(upload))
    } catch (error) {
      setLogoRefused(error instanceof EventLogoError ? error.message : 'This logo can’t be used. Upload a PNG, JPG or WebP.')
    }
  }

  // The header shows the event's logo only while it has one (the API's rule too): without a logo the switch waits, off.
  // The stored choice is sent as it is, so saving doesn't quietly drop it.
  const hasLogo = Boolean(logoPreview)
  const showsLogoInHeader = hasLogo && logoInHeader
  const previewShows = logoPreview !== null && logoPreview !== brokenPreview
  const logoProblem = logoRefused ?? (logoPreview !== null && !previewShows ? LOGO_UNSHOWN : null)

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (saving || !event) return
    // Typed as shown ("Africa/Cape Town"), sent as the zone; empty clears it.
    const zone = timezone.trim().replace(/ /g, '_')
    if (zone && !TIME_ZONES.includes(zone)) {
      setTimezoneError('Choose a time zone from the list.')
      return
    }
    setSaving(true)
    const visibility = listed ? 'listed' : 'unlisted'
    // Every event needs a shortcode, so a cleared field is left out and the server keeps the current one.
    const shortcodeField: Record<string, string> = shortcode.trim() ? { shortcode } : {}
    try {
      const fields: Record<string, string> = {
        name,
        ...shortcodeField,
        subtitle: subtitle || '',
        description: description || '',
        starts_at: fromLocalDatetime(startsAt) ?? '',
        ends_at: fromLocalDatetime(endsAt) ?? '',
        timezone: zone,
        live_only: liveOnly ? '1' : '0',
        visibility,
        logo_in_header: logoInHeader ? '1' : '0',
        ingest_region: ingestRegion,
      }
      let res: { data: EventResource }
      if (logoFile) {
        // Multipart so the new logo rides along with the text fields.
        const formData = new FormData()
        Object.entries(fields).forEach(([key, value]) => formData.append(`event[${key}]`, value))
        formData.append('event[logo]', logoFile)
        res = await api.patchMultipart<{ data: EventResource }>(`/events/${eventId}`, formData)
      } else {
        res = await api.patch<{ data: EventResource }>(`/events/${eventId}`, {
          event: {
            name,
            ...shortcodeField,
            subtitle: subtitle || null,
            description: description || null,
            starts_at: fromLocalDatetime(startsAt),
            ends_at: fromLocalDatetime(endsAt),
            timezone: zone || null,
            live_only: liveOnly,
            visibility,
            logo_in_header: logoInHeader,
            ingest_region: ingestRegion,
          },
        })
      }
      setLogoFile(null)
      toast({ title: 'Saved', description: 'Event settings updated.', tone: 'success' })
      onSaved?.(res.data)
    } catch (err: unknown) {
      const apiErr = err as ApiError
      const detail =
        apiErr.response?.data?.error?.errors?.join('\n') ??
        apiErr.response?.data?.error?.message ??
        'Your settings couldn’t be saved. Try again.'
      toast({ title: 'Settings not saved', description: detail, tone: 'error' })
    } finally {
      setSaving(false)
    }
  }

  // Info tips sit beside each field's label as its `labelAccessory`, outside the label element, so they never join the
  // field's accessible name. The logo has no Play field, so its label row is its own.
  return (
    <form onSubmit={save} className="ps-form">
      <section className="ps-section" aria-labelledby="event-details-heading">
        <h2 id="event-details-heading">Event details</h2>
        <div className="ps-fields">
          <TextField id="event-name" label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
          <IngestRegionPicker value={ingestRegion} onChange={setIngestRegion} />
          <TextField
            id="event-shortcode"
            label="Shortcode"
            labelAccessory={<WorkspaceInfo label="About shortcodes">Your event opens at /e/{shortcode || 'comrades'}.</WorkspaceInfo>}
            value={shortcode}
            onChange={(e) => setShortcode(e.target.value.toLowerCase())}
            placeholder="comrades"
            pattern="[a-z0-9][a-z0-9-]{1,62}[a-z0-9]"
            minLength={3}
            maxLength={64}
            required
          />
          <div className="ps-field">
            <div className="ps-field-label">
              <span id="event-logo-label">Event logo</span>
              <WorkspaceInfo label="About event logos">PNG, JPG, SVG or WebP, up to 1 MB. Shown on the camera joining page, and in your event page’s header if you choose.</WorkspaceInfo>
            </div>
            <div className="ps-logo-row">
              <div className="ps-logo-preview">
                {previewShows ? (
                  <img src={logoPreview} alt="Event logo preview" onError={() => setBrokenPreview(logoPreview)} />
                ) : (
                  <ImageIcon size={24} aria-hidden="true" />
                )}
              </div>
              <IconButton label={logoPreview ? 'Replace logo' : 'Upload logo'} variant="ghost" onClick={() => fileInputRef.current?.click()}>
                <Upload />
              </IconButton>
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" aria-labelledby="event-logo-label" onChange={onLogoSelected} />
            </div>
            {logoProblem && (
              <div className="play-form-field__error" role="alert">
                <CircleAlert aria-hidden="true" />
                <span>{logoProblem}</span>
              </div>
            )}
          </div>
          <Switch
            label="Logo in the header"
            labelAccessory={<WorkspaceInfo label="About the logo in the header">Your event page shows this logo top left, in place of StreamBridge’s.</WorkspaceInfo>}
            description={hasLogo ? undefined : 'Add a logo first.'}
            disabled={!hasLogo}
            checked={showsLogoInHeader}
            onCheckedChange={(checked) => setLogoInHeader(checked)}
          />
        </div>
      </section>

      <section className="ps-section" aria-labelledby="event-schedule-heading">
        <h2 id="event-schedule-heading">Schedule</h2>
        <div className="ps-fields ps-dates">
          <TextField id="event-starts-at" label="Starts at" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
          <TextField id="event-ends-at" label="Ends at" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
          <Autocomplete
            label="Time zone"
            labelAccessory={<WorkspaceInfo label="About the time zone">Where your event happens. Its pre-show pins the event on this city when it has no course.</WorkspaceInfo>}
            value={timezone}
            onValueChange={(next) => {
              setTimezone(next)
              setTimezoneError(null)
            }}
            items={zonesMatching(timezone).filter((zone) => zoneLabel(zone) !== timezone)}
            itemLabel={zoneLabel}
            placeholder="Africa/Johannesburg"
            empty="No time zone matches"
            error={timezoneError ?? undefined}
            name="timezone"
          />
        </div>
      </section>

      <section className="ps-section" aria-labelledby="event-public-heading">
        <h2 id="event-public-heading">Public page</h2>
        <div className="ps-fields">
          <Switch
            label="Show live cameras only"
            labelAccessory={<WorkspaceInfo label="About camera visibility">Hide offline cameras from the public page. Your workspace still shows every camera.</WorkspaceInfo>}
            checked={liveOnly}
            onCheckedChange={(checked) => setLiveOnly(checked)}
          />
          {/* /explore orders listed events by their start, so one without a start time stays off it. */}
          <Switch
            label="Listed on StreamBridge"
            labelAccessory={<WorkspaceInfo label="About listing">Shows your event on StreamBridge’s events page once it has a start time. Anyone with the link can watch either way.</WorkspaceInfo>}
            description={listed && !startsAt ? 'Add a start time to show it on the events page.' : undefined}
            checked={listed}
            onCheckedChange={(checked) => setListed(checked)}
          />
          <TextField id="event-subtitle" label="Subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="A short line beneath your event name" />
          <TextAreaField id="event-description" label="Description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Tell viewers about your event" rows={3} />
        </div>
      </section>
      <div className="ps-save">
        <Button type="submit" disabled={!event} loading={saving}>
          <Save aria-hidden="true" />
          Save settings
        </Button>
      </div>
    </form>
  )
}

function toLocalDatetime(value: unknown): string {
  if (!value || typeof value !== 'string') return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fromLocalDatetime(value: string): string | null {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}
